"use client";

import { useMemo, useRef, useState, type ReactNode } from "react";
import Link from "next/link";
import { toPng } from "html-to-image";
import {
    RiChat1Line,
    RiDownloadLine,
    RiErrorWarningLine,
    RiFileCopyLine,
    RiImageLine,
    RiMapPin2Line,
    RiRefreshLine,
    RiShareLine,
    RiShoppingBag3Line,
    RiTimerLine,
    RiTruckLine,
} from "react-icons/ri";
import { ActionsMenu, Badge, Button, ReceiptCard, Skeleton } from "@dionatanbig2be/big2be-ui";
import { SideDrawer } from "@/shared/components/SideDrawer";
import { MediaImage } from "@/shared/components/MediaImage";
import { toast } from "@/shared/components/toast";
import { OrderStatusIcon } from "./OrderStatusIcon";
import { OrderProgressStepper } from "./OrderProgressStepper";
import {
    buildReceiptSections,
    formatBRL,
    getRefundLabel,
    getStageLabel,
    STATUS_META,
    type OrderStatusMeta,
} from "../../lib/orders.config";
import type { OrderCard, OrderItem } from "../../types/orders";

interface OrderDetailsDrawerProps {
    /** Pedido exibido — pode chegar magro (skeleton no corpo) e hidratar depois. */
    order: OrderCard | null;
    open: boolean;
    onClose: () => void;
    onRepeat: (order: OrderCard) => void;
    repeating: boolean;
}

/**
 * Detalhes do pedido no mesmo shell do carrinho (SideDrawer): faixa de status
 * fixa no topo, corpo rolável com acompanhamento → itens → entrega → resumo, e
 * rodapé fixo com total + "Pedir novamente". O comprovante exportável (PNG)
 * é um ReceiptCard renderizado fora da viewport — texto puro, porque imagens
 * do CDN sem CORS contaminariam o canvas do html-to-image.
 */
export function OrderDetailsDrawer({ order, open, onClose, onRepeat, repeating }: OrderDetailsDrawerProps) {
    const receiptRef = useRef<HTMLDivElement>(null);
    const [sharing, setSharing] = useState(false);

    const sections = useMemo(() => (order?.hydrated ? buildReceiptSections(order) : []), [order]);

    async function exportImage(mode: "download" | "copy") {
        if (!receiptRef.current || !order) return;
        setSharing(true);
        try {
            const dataUrl = await toPng(receiptRef.current, { pixelRatio: 2 });
            if (mode === "download") {
                const a = document.createElement("a");
                a.href = dataUrl;
                a.download = `pedido-${order.id}.png`;
                a.click();
                toast.success("Download iniciado!");
            } else {
                const blob = await (await fetch(dataUrl)).blob();
                await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
                toast.success("Imagem copiada!");
            }
        } catch {
            toast.error("Falha ao gerar imagem");
        } finally {
            setSharing(false);
        }
    }

    const meta = order ? STATUS_META[order.status] : null;
    const canRepeat = Boolean(order?.items?.length);

    return (
        <SideDrawer
            open={open && Boolean(order)}
            onClose={onClose}
            title={order ? `Pedido #${order.id}` : "Pedido"}
            aria-label={order ? `Detalhes do pedido ${order.id}` : "Detalhes do pedido"}
            subheader={order && meta ? <StatusStrip order={order} meta={meta} /> : undefined}
            footer={
                order && order.hydrated && canRepeat ? (
                    <>
                        <div className="flex items-baseline justify-between">
                            <span className="text-[0.92rem] font-semibold text-base-content">Total</span>
                            <span
                                className={`text-xl font-extrabold tabular-nums ${
                                    order.status === "cancelado"
                                        ? "text-base-content/50 line-through"
                                        : "text-primary"
                                }`}
                            >
                                {order.total !== undefined ? formatBRL(order.total) : "—"}
                            </span>
                        </div>
                        <Button
                            type="button"
                            tone="primary"
                            size="lg"
                            width="block"
                            disabled={repeating}
                            isLoading={repeating}
                            onClick={() => onRepeat(order)}
                        >
                            <RiRefreshLine size={16} aria-hidden="true" />
                            {repeating ? "Adicionando…" : "Pedir novamente"}
                        </Button>
                        <ActionsMenu
                            sheetTitle={`Exportar comprovante do pedido ${order.id}`}
                            align="top"
                            trigger={
                                <span className="inline-flex items-center gap-1.5 text-[0.8rem] font-bold text-base-content/55 transition-colors hover:text-primary">
                                    {sharing ? (
                                        <span className="loading loading-spinner loading-xs" />
                                    ) : (
                                        <RiShareLine size={15} aria-hidden="true" />
                                    )}
                                    Exportar comprovante
                                </span>
                            }
                            triggerClassName="self-center"
                            items={[
                                {
                                    label: "Baixar PNG",
                                    icon: <RiDownloadLine size={15} aria-hidden="true" />,
                                    disabled: sharing,
                                    onClick: () => exportImage("download"),
                                },
                                {
                                    label: "Copiar imagem",
                                    icon: <RiFileCopyLine size={15} aria-hidden="true" />,
                                    disabled: sharing,
                                    onClick: () => exportImage("copy"),
                                },
                            ]}
                        />
                    </>
                ) : undefined
            }
        >
            {order && (order.hydrated ? <DrawerBody order={order} /> : <BodySkeleton />)}

            {/* Comprovante exportado — fora da viewport, mas renderizado (o
                html-to-image precisa de layout real; display:none não serve). */}
            {order?.hydrated && (
                <div
                    ref={receiptRef}
                    aria-hidden="true"
                    className="pointer-events-none fixed left-[-200vw] top-0 flex w-[560px] flex-col gap-3 bg-base-100 p-4"
                >
                    <div className={`flex items-center gap-3 rounded-lg px-4 py-3 ${meta?.strip ?? ""}`}>
                        <span className={`flex size-9 items-center justify-center rounded-full ${meta?.disc ?? ""}`}>
                            <OrderStatusIcon status={order.status} size={17} />
                        </span>
                        <div className="min-w-0 flex-1">
                            <div className="text-[0.88rem] font-black tracking-wide">Comprovante do Pedido</div>
                            <div className="mt-[0.15rem] text-[0.8rem] font-bold text-base-content/55">
                                #{order.id}
                                {order.dateLongLabel ? ` · ${order.dateLongLabel}` : ""}
                            </div>
                        </div>
                        {meta && (
                            <Badge tone={meta.tone} variant="soft" size="sm">
                                {getStageLabel(order.rawStatus, order.status)}
                            </Badge>
                        )}
                    </div>
                    <ReceiptCard
                        title="Comprovante do Pedido"
                        sections={sections}
                        classNames={{ root: "w-full max-w-none" }}
                    />
                </div>
            )}
        </SideDrawer>
    );
}

/** Faixa de status fixa sob a barra de título — etapa fina + data + badges. */
function StatusStrip({ order, meta }: { order: OrderCard; meta: OrderStatusMeta }) {
    const refundLabel = getRefundLabel(order.refundStatus ?? null);
    const stage = getStageLabel(order.rawStatus, order.status);
    // Badge do agregado só quando acrescenta algo — "Cancelado" + badge
    // "Cancelado" seria eco puro.
    const showAggregate = meta.label !== stage;
    return (
        <div className={`flex shrink-0 items-center gap-3 border-b border-base-200 px-5 py-3.5 ${meta.strip}`}>
            <span
                aria-hidden="true"
                className={`flex size-10 shrink-0 items-center justify-center rounded-full ${meta.disc}`}
            >
                <OrderStatusIcon status={order.status} size={19} />
            </span>
            <div className="min-w-0 flex-1">
                <div className={`truncate text-[0.92rem] font-extrabold ${meta.text}`}>{stage}</div>
                {order.dateLongLabel && (
                    <div className="mt-0.5 truncate text-[0.75rem] font-semibold text-base-content/50">
                        Realizado em {order.dateLongLabel}
                    </div>
                )}
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
                {showAggregate && (
                    <Badge tone={meta.tone} variant="soft" size="sm">
                        {meta.label}
                    </Badge>
                )}
                {refundLabel && (
                    <Badge tone="neutral" variant="soft" size="sm">
                        {refundLabel}
                    </Badge>
                )}
            </div>
        </div>
    );
}

function DrawerBody({ order }: { order: OrderCard }) {
    const cancelled = order.status === "cancelado";

    return (
        <div className="flex flex-col">
            {/* Acompanhamento */}
            {order.progressStep && !cancelled && (
                <section aria-label="Acompanhamento do pedido" className="border-b border-base-200 px-5 pb-4 pt-3">
                    <SectionTitle icon={<RiTruckLine size={15} aria-hidden="true" />}>
                        Acompanhamento
                    </SectionTitle>
                    <OrderProgressStepper step={order.progressStep} />
                </section>
            )}

            {order.status === "aguardando-pagamento" && (
                <Note tone="warning" icon={<RiTimerLine size={17} aria-hidden="true" />}>
                    Estamos aguardando a confirmação do pagamento. Assim que ela cair, o pedido
                    entra em separação e o acompanhamento aparece aqui.
                </Note>
            )}

            {cancelled && (
                <Note tone="error" icon={<RiErrorWarningLine size={17} aria-hidden="true" />}>
                    Este pedido foi cancelado
                    {order.refundStatus === "reembolsado" ? " e o valor foi reembolsado" : ""}. Se
                    precisar, você pode refazer a compra com os mesmos itens pelo botão abaixo.
                </Note>
            )}

            {/* Itens */}
            <section aria-label="Itens do pedido" className="border-b border-base-200 pb-1 pt-3">
                <div className="px-5">
                    <SectionTitle
                        icon={<RiShoppingBag3Line size={15} aria-hidden="true" />}
                        extra={
                            order.itemCount !== undefined
                                ? `${order.itemCount} ${order.itemCount === 1 ? "item" : "itens"}`
                                : undefined
                        }
                    >
                        Itens do pedido
                    </SectionTitle>
                </div>
                {order.items?.length ? (
                    <ul className="mt-1 list-none">
                        {order.items.map((item) => (
                            <OrderLineItem key={item.id} item={item} cancelled={cancelled} />
                        ))}
                    </ul>
                ) : (
                    <p className="px-5 pb-4 pt-2 text-[0.85rem] text-base-content/50">
                        Não foi possível carregar os itens deste pedido. Tente novamente mais tarde.
                    </p>
                )}
            </section>

            {/* Entrega */}
            {order.deliveryAddress && (
                <section aria-label="Entrega" className="border-b border-base-200 px-5 py-4">
                    <SectionTitle icon={<RiMapPin2Line size={15} aria-hidden="true" />}>Entrega</SectionTitle>
                    <DeliveryInfo order={order} />
                </section>
            )}

            {/* Observação deixada no fechamento */}
            {order.comment && (
                <section aria-label="Observação" className="border-b border-base-200 px-5 py-4">
                    <SectionTitle icon={<RiChat1Line size={15} aria-hidden="true" />}>Observação</SectionTitle>
                    <p className="mt-2 text-[0.85rem] leading-relaxed text-base-content/70">{order.comment}</p>
                </section>
            )}

            {/* Resumo (o total mora no rodapé fixo) */}
            {order.breakdown && (
                <section aria-label="Resumo de valores" className="px-5 py-4">
                    <SectionTitle>Resumo</SectionTitle>
                    <dl className="mt-3 flex flex-col gap-2 text-[0.88rem]">
                        <div className="flex items-baseline justify-between gap-3">
                            <dt className="text-base-content/60">Subtotal</dt>
                            <dd className="tabular-nums text-base-content">
                                {formatBRL(order.breakdown.subtotal)}
                            </dd>
                        </div>
                        {order.breakdown.discount > 0 && (
                            <div className="flex items-baseline justify-between gap-3">
                                <dt className="text-base-content/60">Descontos</dt>
                                <dd className="font-semibold tabular-nums text-success">
                                    − {formatBRL(order.breakdown.discount)}
                                </dd>
                            </div>
                        )}
                    </dl>
                </section>
            )}
        </div>
    );
}

/** Linha de item: imagem | nome (link p/ PDP) + qtd × unitário | total. */
function OrderLineItem({ item, cancelled }: { item: OrderItem; cancelled: boolean }) {
    return (
        <li
            className={`flex items-center gap-3 border-b border-base-200/70 px-5 py-3 last:border-b-0 ${
                cancelled ? "opacity-60" : ""
            }`}
        >
            <figure className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-base-200">
                {item.imageUrl ? (
                    <MediaImage
                        source="products"
                        path={item.imageUrl}
                        alt={item.imageAlt ?? item.name}
                        width={64}
                        height={64}
                        className="object-contain"
                    />
                ) : (
                    <RiImageLine size={22} className="text-base-content/25" aria-hidden="true" />
                )}
            </figure>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                {item.href ? (
                    <Link
                        href={item.href}
                        className="line-clamp-2 text-[0.88rem] font-semibold text-base-content transition-colors hover:text-primary hover:underline"
                    >
                        {item.name}
                    </Link>
                ) : (
                    <span className="line-clamp-2 text-[0.88rem] font-semibold text-base-content">
                        {item.name}
                    </span>
                )}
                {item.variantLabel && (
                    <span className="text-[0.74rem] text-base-content/50">{item.variantLabel}</span>
                )}
                <span className="text-[0.78rem] tabular-nums text-base-content/55">
                    {item.qty}x · {formatBRL(item.unitPrice)}
                    {item.originalUnitPrice !== undefined && (
                        <s className="ml-1.5 text-base-content/40">{formatBRL(item.originalUnitPrice)}</s>
                    )}
                </span>
            </div>
            <span className="shrink-0 text-[0.88rem] font-bold tabular-nums text-base-content">
                {formatBRL(item.unitPrice * item.qty)}
            </span>
        </li>
    );
}

/** Bloco de endereço/método de entrega. */
function DeliveryInfo({ order }: { order: OrderCard }) {
    const addr = order.deliveryAddress!;
    const line = [addr.street, addr.number].filter(Boolean).join(", ");
    const region = [addr.neighborhood, addr.city].filter(Boolean).join(" · ");

    return (
        <div className="mt-2.5 flex flex-col gap-1">
            <span className="text-[0.88rem] font-semibold text-base-content">{addr.label}</span>
            <span className="text-[0.85rem] text-base-content/70">
                {line}
                {addr.complement ? ` (${addr.complement})` : ""}
            </span>
            {(region || addr.zipCode) && (
                <span className="text-[0.82rem] text-base-content/50">
                    {region}
                    {addr.zipCode ? `${region ? " · " : ""}CEP ${addr.zipCode}` : ""}
                </span>
            )}
        </div>
    );
}

/** Micro-título de seção: ícone em primary + rótulo caps + extra à direita. */
function SectionTitle({
    icon,
    extra,
    children,
}: {
    icon?: ReactNode;
    extra?: ReactNode;
    children: ReactNode;
}) {
    return (
        <div className="flex items-center gap-2">
            {icon && (
                <span className="shrink-0 text-primary" aria-hidden="true">
                    {icon}
                </span>
            )}
            <h4 className="text-[0.78rem] font-bold uppercase tracking-[0.06em] text-base-content/55">
                {children}
            </h4>
            {extra && (
                <span className="ml-auto text-[0.78rem] font-semibold text-base-content/45">{extra}</span>
            )}
        </div>
    );
}

/** Nota suave (aguardando pagamento / cancelado). */
function Note({ tone, icon, children }: { tone: "warning" | "error"; icon: ReactNode; children: ReactNode }) {
    const classes =
        tone === "warning" ? "bg-warning/[0.08] text-warning" : "bg-error/[0.07] text-error";
    return (
        <div className="px-5 pt-4">
            <div className={`flex items-start gap-2.5 rounded-xl px-4 py-3 ${classes}`}>
                <span className="mt-0.5 shrink-0" aria-hidden="true">
                    {icon}
                </span>
                <p className="text-[0.83rem] leading-relaxed text-base-content/75">{children}</p>
            </div>
        </div>
    );
}

/** Corpo enquanto os detalhes carregam. */
function BodySkeleton() {
    return (
        <div className="flex flex-col gap-4 px-5 py-4" aria-busy="true" aria-label="Carregando detalhes do pedido">
            <Skeleton className="h-16 w-full rounded-xl" />
            {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                    <Skeleton className="size-16 rounded-xl" />
                    <div className="flex flex-1 flex-col gap-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/3" />
                    </div>
                </div>
            ))}
            <Skeleton className="h-24 w-full rounded-xl" />
        </div>
    );
}
